import React, {useState, useEffect, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import t from 'babel-plugin-yate/macro';

import {getApplication, patchApplication} from '../store/application';
import {postLendingRequestAmlFingerprint} from '../store/request';
import {addModal} from '../store/app';

import {ANAF_CODE} from '../config/employmentStatuses';

import FooterOnline from '../components/FooterOnline';
import RequestNav from '../components/RequestNav';
import logo from '../assets/credex-logo-1.svg';
import Step1 from './Step1';
import Step2 from './Step2';
import Step2Address from './Step2Address';
import Step3 from './Step3';
import Step4 from './Step4';
import ModalConsentCreditBureau from './Step1/ModalConsentCreditBureau';
import ModalEsign from './ModalEsign';
import SendEmail from '../containers/SendEmail';
import ModalInstantPayment from './ModalInstantPayment';
import ModalInfo from '../containers/ModalInfo';
import script from '../lib/script';

const seonJs = 'https://cdn.seondf.com/js/v6/agent.umd.js';
const seonConfig = {
  geolocation: {
    enabled: true,
    canPrompt: false,
  },
  silentMode: true,
  dnsResolverDomain: 'seondfresolver.com',
};
const cbModalId = 'credit_bureau';
const signatureModalId = 'online_signature';
const instantPaymentModalId = 'instant_payment';
const infoModalId = 'info';
let tick = null;

function OnlineApplication() {
  const dispatch = useDispatch();
  const application = useSelector(state => state.application);
  const {uuid} = useParams();
  const inProgress = application.in_progress;
  const orderOrigin = application.location_id;
  const status = application.status;
  const timeStamp = application.time_stamp ?? new Date();
  const professionalStatus = application.customer?.professional_status?.length
    ? application.customer.professional_status
    : [
        /**
         * if the 'professional_status' is not received from Altex or MediaGalaxy,
         * 'Employee with ANAF' is checked by default
         * */
        {code: ANAF_CODE},
      ];
  const [currentStep, setCurrentStep] = useState();
  const [isApplicationLoading, setIsApplicationLoading] = useState(true);

  const submitData = useCallback(
    async (values, isPatchSkipped) => {
      /**
       * 'isPatchSkipped' is sent as second argument in step three, as soon as the upload succeeded,
       * in order to have the latest value for the field 'checklist.income_uploaded'
       * */
      if (isPatchSkipped !== true) {
        await dispatch(patchApplication(values, uuid));
      }

      dispatch(getApplication(uuid));

      const session = await window.seon.getSession(seonConfig);
      if (session) {
        dispatch(postLendingRequestAmlFingerprint({session}, uuid));
      }
    },
    [dispatch, uuid],
  );

  const instantPaymentRedirect = useCallback(async () => {
    if (application.payment === 'card') {
      return dispatch(addModal(instantPaymentModalId));
    }

    return dispatch(addModal(infoModalId));
  }, [dispatch, application.payment]);

  useEffect(() => {
    dispatch(getApplication(uuid));
    setIsApplicationLoading(false);
  }, [dispatch, uuid]);

  useEffect(() => {
    (async function () {
      await script('seon-js', seonJs);
      window.seon.init();
    })();
  }, []);

  useEffect(() => {
    if (inProgress) {
      dispatch(addModal(infoModalId));
      setCurrentStep();
      return;
    }

    switch (status) {
      case 'new':
        setCurrentStep(1);
        break;
      case 'awaiting_cb_consent':
        dispatch(addModal(cbModalId));
        break;
      case 'awaiting_kyc':
      case 'ocr':
      case 'selfie':
      case 'kyc_ok_awaiting_address':
        setCurrentStep(2);
        break;
      case 'awaiting_anaf_consent':
        dispatch(addModal(signatureModalId));
        break;
      case 'kyc_ok':
      case 'awaiting_anaf':
      case 'anaf_ok':
        setCurrentStep(3);
        break;
      case 'awaiting_cb':
      case 'manual_analysis':
      case 'risk_analysis':
      case 'risk_manual_analysis':
      case 'risk_validated':
      case 'analysis':
      case 'rejected':
      case 'canceled':
      case 'expired':
      case 'duplicate_contract':
      case 'done':
        dispatch(addModal(infoModalId));
        break;
      case 'signed':
        instantPaymentRedirect();
        break;
      case 'cb_ok':
        dispatch(addModal(infoModalId));
        submitData({});
        break;
      case 'approved':
        setCurrentStep(4);
        break;
      case 'awaiting_esign':
        dispatch(addModal(signatureModalId));
        setCurrentStep(4);
        break;
      default:
        setCurrentStep();
    }
  }, [instantPaymentRedirect, dispatch, inProgress, submitData, status]);

  useEffect(() => {
    clearInterval(tick);

    const delay =
      [
        'awaiting_cb',
        'analysis',
        'manual_analysis',
        'risk_analysis',
        'risk_manual_analysis',
        'risk_validated',
        'approved',
      ].includes(status) ||
      inProgress ||
      (['signed'].includes(status) && application.payment === 'card')
        ? 10000
        : null;

    if (!delay) {
      return;
    }

    tick = setInterval(() => dispatch(getApplication(uuid)), delay);

    return () => clearInterval(tick);
  }, [application, dispatch, professionalStatus, status, uuid, inProgress]);

  return isApplicationLoading ? (
    <div className="flex items-center justify-center relative h-screen text-20">
      <div className="spinner" />
    </div>
  ) : (
    <>
      <div className="pt-4">
        <img
          className="mx-auto"
          src={logo}
          alt="Credex logo"
          width="141"
          height="40"
        />

        {['awaiting_kyc', 'ocr', 'selfie'].includes(status) && (
          <SendEmail id={uuid} type="mobile" />
        )}
      </div>

      <div className="mx-auto">
        <RequestNav currentStep={currentStep} lendingTypeId={4} />

        <div style={{minHeight: 'calc(100vh - 260px)'}}>
          {([
            'awaiting_cb',
            'cb_ok',
            'analysis',
            'manual_analysis',
            'risk_analysis',
            'risk_manual_analysis',
            'risk_validated',
            'signed',
            'done',
            'rejected',
            'canceled',
            'expired',
            'duplicate_contract',
          ].includes(status) ||
            inProgress) && (
            <ModalInfo
              data={application}
              id={infoModalId}
              status={status}
              timeStamp={timeStamp}
              appType="online"
            />
          )}

          {status === 'awaiting_cb_consent' && (
            <ModalConsentCreditBureau
              id={cbModalId}
              data={application}
              patchHandler={submitData}
            />
          )}

          {['awaiting_anaf_consent', 'awaiting_esign'].includes(status) && (
            <ModalEsign
              id={signatureModalId}
              header={t`Bine ai venit! Credex – te ajuta sa semnezi digital`}
              data={application}
              uuid={uuid}
              status={status}
              patchHandler={submitData}
              inProgress={inProgress}
            />
          )}

          {['signed'].includes(status) && (
            <ModalInstantPayment
              id={instantPaymentModalId}
              header={t`Bine ai venit! Credex – te ajuta sa primesti banii instant!`}
              data={application}
              uuid={uuid}
              appType="online"
            />
          )}

          {!inProgress && (
            <>
              {['new'].includes(status) && (
                <Step1
                  data={application}
                  uuid={uuid}
                  patchHandler={submitData}
                />
              )}

              {['awaiting_kyc', 'ocr', 'selfie'].includes(status) && (
                <Step2
                  data={application}
                  uuid={uuid}
                  status={status}
                  patchHandler={submitData}
                />
              )}

              {['kyc_ok_awaiting_address'].includes(status) && (
                <Step2Address
                  data={application}
                  uuid={uuid}
                  status={status}
                  patchHandler={submitData}
                />
              )}

              {['kyc_ok', 'awaiting_anaf', 'anaf_ok'].includes(status) && (
                <Step3
                  data={application}
                  uuid={uuid}
                  status={status}
                  patchHandler={submitData}
                />
              )}

              {['approved'].includes(status) && (
                <Step4
                  data={application}
                  uuid={uuid}
                  patchHandler={submitData}
                />
              )}
            </>
          )}
        </div>

        {
          /**
           * only when the application's data is fully retrieved
           * render the content, to avoid the color flickering
           * */
          Boolean(application.status) && (
            <FooterOnline orderOrigin={orderOrigin} />
          )
        }
      </div>
    </>
  );
}

export default OnlineApplication;
